import { Wordmark } from "./Wordmark";
import { LOCKUP_ARIA, PEE_ARIA, PEE_FULL_NAME, SYSTEM_DESCRIPTOR } from "@/lib/looseend/pee";
import { cn } from "@/lib/utils";

type Variant = "full" | "compact" | "wordmark";

/**
 * The single source of truth for the official L∞seEnd™ brand lockup:
 *
 *   L∞seEnd™ / Personal Execution System / Powered by PEE™ / Personal Execution Engine
 *
 * Never hand-format these lines elsewhere — use this component so future brand
 * refinements stay central.
 */
export function LooseEndBrandLockup({
  variant = "compact",
  orientation = "vertical",
  showSystemDescriptor,
  showPoweredBy,
  showEngineExpansion,
  wordmarkSize,
  className,
}: {
  variant?: Variant;
  orientation?: "vertical" | "horizontal";
  showSystemDescriptor?: boolean;
  showPoweredBy?: boolean;
  showEngineExpansion?: boolean;
  wordmarkSize?: "sm" | "md" | "lg" | "xl";
  className?: string;
}) {
  const system = showSystemDescriptor ?? variant !== "wordmark";
  const powered = showPoweredBy ?? variant !== "wordmark";
  const expansion = showEngineExpansion ?? variant === "full";
  const size = wordmarkSize ?? (variant === "full" ? "lg" : "md");

  return (
    <div
      className={cn(
        "min-w-0",
        orientation === "horizontal" ? "flex flex-wrap items-baseline gap-x-2 gap-y-0.5" : "",
        className,
      )}
    >
      <span className="sr-only">{LOCKUP_ARIA}</span>
      <Wordmark size={size} aria-hidden="true" />
      {system ? (
        <p
          aria-hidden="true"
          className="mt-0.5 text-[12px] font-medium tracking-[0.01em] text-foreground/80"
        >
          {SYSTEM_DESCRIPTOR}
        </p>
      ) : null}
      {powered ? (
        <p
          aria-hidden="true"
          className={cn("text-[11px] text-muted-foreground", system ? "mt-1" : "mt-0.5")}
        >
          Powered by <span className="text-primary">PEE™</span>
        </p>
      ) : null}
      {expansion ? (
        <p aria-hidden="true" className="text-[10px] text-muted-foreground/80">
          {PEE_FULL_NAME}
        </p>
      ) : null}
    </div>
  );
}

/** Tiny inline product descriptor for tight, working-interface contexts. */
export function SystemDescriptorLine({ className }: { className?: string }) {
  return (
    <p className={cn("text-[11px] tracking-[0.01em] text-muted-foreground", className)}>
      <span className="sr-only">{PEE_ARIA} — </span>
      {SYSTEM_DESCRIPTOR}
    </p>
  );
}
